import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { io } from 'socket.io-client';

import useApiService from './ApiService';

import { getDateRangeByPeriod } from '../Components/DateRangeSelector';
import { useAuth } from '../Contexts/AuthContext';
import useLatestRequest from '../Hooks/useLatestRequest';
import CONFIG from './CONFIG';

const ThreadApiContext = createContext(null);

const socketAddress = () => {
  return new URL(CONFIG.SERVER_CHANNEL).origin;
};

const socketPath = () => {
  if (CONFIG.SERVER_CHANNEL.endsWith('/channel_api')) {
    return '/channel_api' + CONFIG.SOCKET_ROUTE;
  } else {
    return CONFIG.SOCKET_ROUTE;
  }
};

export function ThreadApiProvider({ children }) {
  const { get, post, put, del } = useApiService(CONFIG.SERVER_CHANNEL);
  const { getAccessTokenSilently } = useAuth();

  const navigate = useNavigate();

  const { organizationId, chatId } = useParams();

  const [isOnline, setIsOnline] = useState(false);

  const beginThreadsRequest = useLatestRequest();

  const [filters, setFilters] = useState({
    skip: 0,
    limit: 15,
    timeRange: 'max',
    startDate: undefined,
    endDate: undefined,
    isActive: true,
    isVirtual: undefined,
    isModerated: undefined,
    channelKeys: [],
    searchName: '',
    searchMessageText: '',
  });

  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [haveMoreThreads, setHaveMoreThreads] = useState(false);
  const [threads, setThreads] = useState([]);
  const [totalThreads, setTotalThreads] = useState(0);
  const [thread, setThread] = useState();
  const [isTyping, setIsTyping] = useState(false);

  const [threadMessages, setThreadMessages] = useState([]);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const fetchingThreadId = useRef(null);

  const [socket, setSocket] = useState(
    io(socketAddress(), {
      transports: ['websocket'],
      path: socketPath(),
      autoConnect: false,
      upgrade: false,
      auth: (cb) => {
        getAccessTokenSilently().then((token) => {
          cb({
            auth_token: token,
            organization_id: organizationId,
          });
        });
      },
    }),
  );

  useEffect(() => {
    return () => {
      if (socket) {
        console.log('Cleaning up socket connection...');
        socket.off('connect');
        socket.off('disconnect');
        socket.off('stream');
        socket.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    if (!chatId) {
      setThread(undefined);
      return;
    }

    const foundThread = threads.find((t) => t.id === chatId);
    if (foundThread) {
      fetchingThreadId.current = null;
      setThread(foundThread);
    } else if (!loading && fetchingThreadId.current !== chatId) {
      const currentId = chatId;
      fetchingThreadId.current = currentId;

      fetchThread(currentId)
        .then((fetchedThread) => {
          if (fetchingThreadId.current === currentId) {
            setThread(fetchedThread);
          }
        })
        .finally(() => {
          if (fetchingThreadId.current === currentId) {
            fetchingThreadId.current = null;
          }
        });
    }
  }, [chatId, threads, loading]);

  useEffect(() => {
    updateFiltersAndFetch();
  }, []);

  useEffect(() => {
    const f = async () => {
      if (!chatId) {
        return;
      }

      setLoadingMessages(true);
      setLoadingAnalysis(true);
      setAnalysisResults(null);

      try {
        const [messagesResponse, analysisResponse] = await Promise.all([
          getThreadMessages(chatId),
          getAnalysisResults(chatId).catch(() => null),
        ]);

        setThreadMessages(messagesResponse.items);

        if (analysisResponse && analysisResponse.items && analysisResponse.items.length > 0) {
          setAnalysisResults(analysisResponse.items);
        }
      } catch (error) {
        console.error('Error fetching thread messages:', error);
      } finally {
        setLoadingMessages(false);
        setLoadingAnalysis(false);
      }

      socket.off('connect');
      socket.off('disconnect');
      socket.off('stream');

      socket.on('connect', () => {
        setIsOnline(true);
        console.log('Connected to socket server');
        getAccessTokenSilently().then((token) => {
          socket.emit('handshake', {
            auth_token: token,
            organization_id: organizationId,
          });
        });
      });
      socket.on('disconnect', () => {
        setIsOnline(false);
        console.log('Disconnected from socket server');
      });
      socket.on('stream', (message) => {
        console.log('Received message:', message);

        if (message?.type === 'message_reactions') {
          const messageId = message.message_id;

          const reactions = Array.isArray(message.reactions) ? message.reactions : [];

          if (!messageId) return;

          console.log('messageid', messageId);
          console.log('reactions', reactions);

          setThreadMessages((prev) =>
            prev.map((m) => {
              const mId = String(m.id || '');

              if (mId === messageId) {
                return { ...m, reactions };
              }
              return m;
            }),
          );

          console.log('all oke');

          return;
        }

        if (message.channel === '') {
          setThreads((prev) => {
            if (!prev.some((t) => t.id === message.chatId)) {
              setTimeout(() => {
                getThread(message.chatId);
              }, 500);
            }
            return prev.map((t) => {
              if (t.id === message.chatId) {
                const existingIndex = t.last_message.findIndex((msg) => msg.id === message.id);

                if (existingIndex !== -1) {
                  const updatedMessages = [...t.last_message];
                  updatedMessages[existingIndex] = {
                    ...updatedMessages[existingIndex],
                    properties: message.properties,
                    triggers: message.triggers,
                    thematics: message.thematics,
                  };
                  return { ...t, last_message: updatedMessages };
                } else {
                  return { ...t, last_message: [message, ...t.last_message] };
                }
              }
              return t;
            });
          });

          if (message.chatId === chatId) {
            setThreadMessages((prev) => {
              const exists = prev.some((m) => m.id === message.id);
              return exists
                ? prev.map((m) => (m.id === message.id ? { ...m, ...message } : m))
                : [...prev, message];
            });
          }
        } else if (message.channel === 'technicalMessages') {
          if (message.type === 'THREAD_UPDATED') {
            setThreads((prev) => [
              ...prev.map((t) => ({
                ...t,
                is_virtual: t.id === message.chatId ? message.is_virtual : t.is_virtual,
                is_active: t.id === message.chatId ? message.is_active : t.is_active,
                is_moderated: t.id === message.chatId ? message.is_moderated : t.is_moderated,
                is_waiting: t.id === message.chatId ? message.is_waiting : t.is_waiting,
                dequeue_on: t.id === message.chatId ? message.dequeue_on : t.dequeue_on,
                name: t.id === message.chatId ? message.user_name || t.name : t.name,
                phone: t.id === message.chatId ? message.user_phone || t.phone : t.phone,
              })),
            ]);
          } else if (message.type === 'USER_UPDATED') {
            setThreads((prev) => [
              ...prev.map((t) => ({
                ...t,
                name: t.id === message.chatId ? message.user_name || t.name : t.name,
                phone: t.id === message.chatId ? message.user_phone || t.phone : t.phone,
              })),
            ]);
          } else if (
            message.type === 'typing' &&
            message.typing === 'start' &&
            message.chatId === chatId
          ) {
            setIsTyping(true);
          } else if (
            message.type === 'typing' &&
            message.typing === 'stop' &&
            message.chatId === chatId
          ) {
            setIsTyping(false);
          }
        } else {
          console.warn('Unknown message channel:', message.channel);
        }
      });

      if (!socket.connected) {
        socket.connect();
      }
    };

    f();
  }, [chatId]);

  const getThreads = async (
    skip = 0,
    limit = 10,
    startDate,
    endDate,
    isActive,
    isVirtual,
    isModerated,
    channelKeys,
  ) => {
    try {
      const params = new URLSearchParams();

      params.append('skip', String(skip));
      params.append('limit', String(limit));

      if (startDate) {
        params.append('start_date', startDate);
      }
      if (endDate) {
        params.append('end_date', endDate);
      }
      if (isActive !== undefined) {
        params.append('is_active', String(isActive));
      }
      if (isVirtual !== undefined) {
        params.append('is_virtual', String(isVirtual));
      }
      if (isModerated !== undefined) {
        params.append('is_moderated', String(isModerated));
      }
      if (channelKeys && channelKeys.length > 0) {
        channelKeys.forEach((key) => params.append('channel_keys', key));
      }

      const response = await get(`/v1/conversations/all?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching threads:', error);
      throw error;
    }
  };

  const searchThreads = async (
    name,
    message,
    skip = 0,
    limit = 10,
    startDate,
    endDate,
    threadIsActive,
    threadIsVirtual,
    threadIsModerated,
    channelKeys,
  ) => {
    try {
      const response = await post(`/v1/conversations/search`, {
        name: name,
        message: message,
        skip: skip,
        limit: limit,
        start_date: startDate,
        end_date: endDate,
        is_active: threadIsActive,
        is_virtual: threadIsVirtual,
        is_moderated: threadIsModerated,
        channel_keys: channelKeys,
      });
      return response.data;
    } catch (error) {
      console.error('Error searching threads:', error);
      throw error;
    }
  };

  const updateFiltersAndFetch = async (newFilters = {}, resetSkip = false) => {
    const isLatest = beginThreadsRequest();
    const updatedFilters = {
      ...filters,
      ...newFilters,
      skip: resetSkip ? 0 : (newFilters.skip ?? filters.skip),
    };
    if (updatedFilters.skip === 0 && updatedFilters.timeRange) {
      const range = getDateRangeByPeriod(updatedFilters.timeRange);
      if (range) {
        updatedFilters.startDate = range.start_date;
        updatedFilters.endDate = range.end_date;
      }
    }
    setFilters(updatedFilters);
    setLoading(true);

    try {
      const args = [
        updatedFilters.skip,
        updatedFilters.limit,
        updatedFilters.startDate,
        updatedFilters.endDate,
        updatedFilters.isActive,
        updatedFilters.isVirtual,
        updatedFilters.isModerated,
        updatedFilters.channelKeys,
      ];

      const response =
        updatedFilters.searchName || updatedFilters.searchMessageText
          ? await searchThreads(
              updatedFilters.searchName,
              updatedFilters.searchMessageText,
              ...args,
            )
          : await getThreads(...args);
      if (!isLatest()) return;

      setThreads((prev) => (resetSkip ? response.items : [...prev, ...response.items]));
      setTotalThreads(response.count);
      setHaveMoreThreads(response.count > updatedFilters.skip + updatedFilters.limit);
    } catch (error) {
      if (!isLatest()) return;
      console.error('Error fetching threads:', error);
    } finally {
      if (isLatest()) setLoading(false);
    }
  };

  const loadMore = () => {
    updateFiltersAndFetch({ skip: filters.skip + filters.limit }, false);
  };

  const fetchThread = async (threadId) => {
    const response = await get(`/v1/conversations/all/${threadId}`);
    return response.data;
  };

  const getThread = async (threadId) => {
    const thread = await fetchThread(threadId);
    setThreads((prev) => {
      const existing = prev.find((t) => t.id === thread.id);

      const merged = existing
        ? {
            ...existing,
            ...thread,
            name: thread.name?.trim() || existing.name,
            phone: thread.phone || existing.phone,
          }
        : thread;

      return [merged, ...prev.filter((t) => t.id !== thread.id)];
    });
    setTotalThreads((prev) => prev + 1);
  };

  const closeThread = async () => {
    try {
      const response = await post(`/v1/conversations/all/${thread.id}/is_active`, {
        is_active: false,
      });

      setThreads((prev) => [
        ...prev
          .filter((t) => filters.isActive !== false && t.id !== thread.id)
          .map((t) => ({
            ...t,
            is_active: t.id === response.data.id ? response.data.is_active : t.is_active,
          })),
      ]);

      setTimeout(() => {
        if (filters.isActive) {
          navigate(
            threads.length > 0
              ? `/organizations/${organizationId}/chats/${threads[0].id}`
              : `/organizations/${organizationId}/chats`,
          );
          setTotalThreads((prev) => prev - 1);
        }
      }, 0);

      return response.data;
    } catch (error) {
      console.error('Error closing thread:', error);
      throw error;
    }
  };

  const openThread = async () => {
    try {
      const response = await post(`/v1/conversations/all/${thread.id}/is_active`, {
        is_active: true,
      });

      return response.data;
    } catch (error) {
      console.error('Error opening thread:', error);
      throw error;
    }
  };

  const setVirtualThread = async (virtual) => {
    try {
      const response = await post(`/v1/conversations/all/${thread.id}/is_virtual`, {
        is_virtual: virtual,
      });
      setThreads((prev) => [
        ...prev.map((t) =>
          t.id === response.data.id
            ? {
                ...t,
                is_virtual: response.data.is_virtual,
                is_waiting: response.data.is_waiting,
                dequeue_on: response.data.dequeue_on,
              }
            : t,
        ),
      ]);
      return response.data;
    } catch (error) {
      console.error('Error setting thread as virtual:', error);
      throw error;
    }
  };

  const setModeratedThread = async (moderated) => {
    try {
      const response = await post(`/v1/conversations/all/${thread.id}/is_moderated`, {
        is_moderated: moderated,
      });
      setThreads((prev) => [
        ...prev.map((t) => ({
          ...t,
          is_moderated: t.id === response.data.id ? response.data.is_moderated : t.is_moderated,
        })),
      ]);
      return response.data;
    } catch (error) {
      console.error('Error setting thread as moderated:', error);
      throw error;
    }
  };

  const replyToThreadWithAi = async () => {
    try {
      const response = await post(`/v1/conversations/all/${thread.id}/reply`);
      setThreads((prev) => [
        ...prev.map((t) =>
          t.id === response.data.id
            ? {
                ...t,
                is_virtual: response.data.is_virtual,
                is_waiting: response.data.is_waiting,
                dequeue_on: response.data.dequeue_on,
              }
            : t,
        ),
      ]);

      return response.data;
    } catch (error) {
      console.error('Error replying to thread with AI:', error);
      throw error;
    }
  };

  const peekThreadFromQueue = async () => {
    try {
      const response = await post(`/v1/conversations/all/${thread.id}/peek_from_queue`);
      setThreads((prev) => [
        ...prev.map((t) => ({
          ...t,
          is_waiting: t.id === response.data.id ? response.data.is_waiting : t.is_waiting,
          dequeue_on: t.id === response.data.id ? response.data.dequeue_on : t.dequeue_on,
        })),
      ]);

      return response.data;
    } catch (error) {
      console.error('Error replying to thread with AI:', error);
      throw error;
    }
  };

  const deleteThread = async () => {
    try {
      const response = await del(`/v1/conversations/all/${thread.id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting thread:', error);
      throw error;
    }
  };

  const getThreadMessages = async (threadId, skip, limit) => {
    if (!skip) {
      skip = 0;
    }
    if (!limit) {
      limit = 1000;
    }

    try {
      const response = await get(
        `/v1/conversations/all/${threadId}/messages?skip=${skip}&limit=${limit}`,
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching thread messages:', error);
      throw error;
    }
  };

  const getAnalysisResults = async (threadId) => {
    try {
      const response = await get(`/v1/post-call-analysis/threads/${threadId}/results`);
      return response.data;
    } catch (error) {
      console.error('Error fetching analysis results:', error);
      throw error;
    }
  };

  const deleteMessage = async (messageId) => {
    try {
      const response = await del(`/v1/conversations/all/any/messages/${messageId}`);
      setThreadMessages((prev) => [...prev.filter((t) => t.id !== messageId)]);

      return response.data;
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  };

  const applyMessage = async (messageId, messageText, messageAttachments, uploadAttachments) => {
    try {
      console.log(
        'Applying message:',
        messageId,
        messageText,
        messageAttachments,
        uploadAttachments,
      );

      const response = await post(`/v1/conversations/all/any/messages/${messageId}/apply`, {
        text: messageText,
        attachments: [
          ...messageAttachments.images,
          ...messageAttachments.files,
          ...messageAttachments.audios,
        ],
        upload_attachments: uploadAttachments,
      });
      setThreadMessages((prev) => [
        ...prev.map((t) => ({
          ...t,
          isSent: t.id === messageId ? true : t.isSent,
          text: t.id === messageId && messageText ? messageText : t.text,
          images:
            t.id === messageId && messageAttachments.images ? messageAttachments.images : t.images,
          files:
            t.id === messageId && messageAttachments.files ? messageAttachments.files : t.files,
          audio:
            t.id === messageId && messageAttachments.audios ? messageAttachments.audios : t.audio,
        })),
      ]);
    } catch (error) {
      console.error('Error applying message:', error);
      throw error;
    }
  };

  const patchMessageInThread = (updatedMessage) => {
    if (!updatedMessage || !updatedMessage.id) return;
    setThreadMessages((prev) =>
      prev.map((m) => (m.id === updatedMessage.id ? { ...m, ...updatedMessage } : m)),
    );
  };

  const setMessageReaction = async (messageId, reaction) => {
    try {
      const response = await post(`/v1/conversations/all/any/messages/${messageId}/add_reactions`, {
        reaction: reaction,
      });
      patchMessageInThread(response.data);
      return response.data;
    } catch (error) {
      console.error('Error setting message reaction:', error);
      throw error;
    }
  };

  const deleteMessageReaction = async (messageId, reaction) => {
    try {
      const response = await post(
        `/v1/conversations/all/any/messages/${messageId}/delete_reactions`,
        {
          reaction: reaction,
        },
      );
      patchMessageInThread(response.data);
      return response.data;
    } catch (error) {
      console.error('Error deleting message reaction:', error);
      throw error;
    }
  };

  const sendMessage = async (message) => {
    console.log('Sending message: ', message);

    if (!thread) {
      console.error('Thread is not defined.');
      return;
    }

    const newMessage = {
      ...message,
      id: message.id || `msg${Date.now()}`,
      chatId: thread.id,
      // user,
      sure_channel: thread.channel.id,
      type: 'MESSAGE',
      thread_key: thread.id,
      auth_token: await getAccessTokenSilently(),
      timestamp: message.timestamp,
      organization_id: organizationId,
    };

    // Handle uploads (audio, files, etc.) if needed:
    if (newMessage.typeOfMedia === 'audio_message' && newMessage.file?.startsWith('blob:')) {
      try {
        const response = await fetch(newMessage.file);
        const blob = await response.blob();
        const fileName = `recorded_audio_${Date.now()}.mp4`;
        console.log('✅ Audio file processed:', fileName);
        newMessage.file = fileName;
      } catch (error) {
        console.error('❌ Error processing audio file:', error);
      }
    } else if (newMessage.typeOfMedia === 'file') {
      const multiFiles = newMessage.multipleFiles || [];
      for (let f of multiFiles) {
        if (f.url?.startsWith('blob:')) {
          try {
            const response = await fetch(f.url);
            const blob = await response.blob();
            // Typically you'd upload or handle the file buffer
            console.log('✅ File processed:', f.fileName);
          } catch (err) {
            console.error('Error uploading file:', f.fileName, err);
          }
        }
      }
    }

    if (socket?.connected) {
      socket.emit('stream', newMessage);
    } else {
      console.warn('Socket not connected.');
    }
  };

  return (
    <ThreadApiContext.Provider
      value={{
        filters,
        updateFiltersAndFetch,
        loadMore,

        isOnline,
        isTyping,

        loading,
        threads,
        totalThreads,
        haveMoreThreads,

        thread,

        threadMessages,
        loadingMessages,

        analysisResults,
        loadingAnalysis,

        getThreads,
        searchThreads,
        closeThread,
        openThread,
        deleteThread,
        setVirtualThread,
        setModeratedThread,
        replyToThreadWithAi,
        peekThreadFromQueue,

        getThreadMessages,
        deleteMessage,
        applyMessage,
        sendMessage,
        setMessageReaction,
        deleteMessageReaction,
      }}
    >
      {children}
    </ThreadApiContext.Provider>
  );
}

export function useThreadApi() {
  const context = useContext(ThreadApiContext);

  if (!context) {
    throw new Error('useThreadApi must be used within a ThreadApiProvider');
  }

  return context;
}
